import { useState } from "react";
import type { PlanEvent, Task } from "../../../shared/types";
import { CATEGORY_BAR_COLORS, CATEGORY_LABELS, PRIORITY_COLORS } from "../../../shared/types";
import { Sun, CheckSquare, Flame, CalendarIcon, CircleDotIcon, CircleIcon, ClockIcon, ZapIcon } from "../../../ui/icons";

const TODAY = new Date();
const FORMATTED = TODAY.toLocaleDateString("fr-FR", {
  weekday: "long",
  day: "numeric",
  month: "long",
});
const GREETING_BASE = TODAY.getHours() < 12 ? "Bonjour" : TODAY.getHours() < 18 ? "Bon après-midi" : "Bonsoir";
const CAPITALIZED = FORMATTED.charAt(0).toUpperCase() + FORMATTED.slice(1);

interface HeroPreviewProps {
  readonly todayEvents: PlanEvent[];
  readonly doingTasks: Task[];
}

interface NavItemProps {
  readonly icon: React.ReactNode;
  readonly label: string;
  readonly active?: boolean;
}

function NavItem({ icon, label, active }: NavItemProps) {
  return (
    <div className={`flex flex-col items-center gap-0.5 px-2 ${active ? "text-accent" : "text-ink-faint"}`}>
      {icon}
      <span className="text-[8px] font-medium">{label}</span>
    </div>
  );
}

export function HeroPreview({ todayEvents, doingTasks }: HeroPreviewProps) {
  const [name, setName] = useState("John");
  const [isEditing, setIsEditing] = useState(false);
  const visibleEvents = todayEvents.slice(0, 2);
  const doing = doingTasks.filter((t) => t.status === "doing").slice(0, 1);
  const todo = doingTasks.filter((t) => t.status === "todo").slice(0, 2);

  return (
    <div
      className="bg-surface border border-border rounded-2xl shadow-lg overflow-hidden
        w-full max-w-sm mx-auto md:mx-0 flex flex-col"
    >
      {/* Header */}
      <div className="px-5 pt-4 pb-3 border-b border-border/50">
        <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0">
          <span className="text-sm font-medium text-ink">
            {GREETING_BASE},{" "}
            {isEditing ? (
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setIsEditing(false)}
                onKeyDown={(e) => { if (e.key === "Enter") setIsEditing(false); }}
                autoFocus
                className="inline-block w-24 bg-transparent border-b border-accent text-sm font-medium text-ink focus:outline-none"
              />
            ) : (
              <span
                onClick={() => setIsEditing(true)}
                className="cursor-default"
              >
                {name || "John"}
              </span>
            )}
            <span className="text-ink-muted mx-1">·</span>
            <span className="text-xs text-ink-muted">{CAPITALIZED}</span>
          </span>
        </div>
      </div>

      {/* Events */}
      {visibleEvents.length > 0 && (
        <div className="px-5 pt-3 pb-2">
          <h3 className="flex items-center gap-1.5 text-[10px] font-semibold text-ink-faint uppercase tracking-wide mb-2">
            <CalendarIcon className="w-3 h-3" />
            Événements
          </h3>
          <div className="space-y-1.5">
            {visibleEvents.map((e) => (
              <div key={e.id} className="relative bg-surface-alt border border-border/60 rounded-lg overflow-hidden">
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${CATEGORY_BAR_COLORS[e.category]}`} />
                <div className="flex items-center gap-2 pl-3.5 pr-3 py-2">
                  <span className="text-[10px] text-ink-faint font-mono tabular-nums">{e.startTime}</span>
                  <span className="text-xs font-medium text-ink flex-1 truncate">{e.title}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Doing tasks */}
      {doing.length > 0 && (
        <div className={`px-5 pt-2 ${todo.length > 0 ? "pb-1" : "pb-3"}`}>
          <h3 className="flex items-center gap-1.5 text-[10px] font-semibold text-ink-faint uppercase tracking-wide mb-2">
            <ZapIcon className="w-3 h-3" />
            En cours
          </h3>
          <div className="space-y-1.5">
            {doing.map((t) => (
              <div key={t.id} className="relative bg-surface-alt border border-border/60 rounded-lg overflow-hidden">
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${CATEGORY_BAR_COLORS[t.category]}`} />
                <div className="flex items-center gap-2 pl-3.5 pr-3 py-2">
                  <CircleDotIcon className="w-3.5 h-3.5 text-accent shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-ink truncate block">{t.title}</span>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[9px] text-ink-faint">{CATEGORY_LABELS[t.category]}</span>
                      {t.priority && t.priority !== "normal" && (
                        <span className={`text-[9px] font-medium uppercase ${PRIORITY_COLORS[t.priority]}`}>
                          {t.priority}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Todo tasks */}
      {todo.length > 0 && (
        <div className="px-5 pt-2 pb-5">
          <h3 className="flex items-center gap-1.5 text-[10px] font-semibold text-ink-faint uppercase tracking-wide mb-2">
            <ClockIcon className="w-3 h-3" />
            À faire
          </h3>
          <div className="space-y-1.5">
            {todo.map((t) => (
              <div key={t.id} className="relative bg-surface-alt border border-border/60 rounded-lg overflow-hidden">
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${CATEGORY_BAR_COLORS[t.category]}`} />
                <div className="flex items-center gap-2 pl-3.5 pr-3 py-2">
                  <CircleIcon className="w-3.5 h-3.5 text-ink-muted shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-ink truncate block">{t.title}</span>
                    <span className="text-[9px] text-ink-faint">{CATEGORY_LABELS[t.category]}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bottom nav */}
      <div className="mt-auto px-4 py-2.5 border-t border-border/50 flex justify-around">
        <NavItem icon={<Sun className="w-3.5 h-3.5" />} label="Aujourd'hui" active />
        <NavItem icon={<CalendarIcon className="w-3.5 h-3.5" />} label="Calendrier" />
        <NavItem icon={<CheckSquare className="w-3.5 h-3.5" />} label="Tâches" />
        <NavItem icon={<Flame className="w-3.5 h-3.5" />} label="Habitudes" />
      </div>
    </div>
  );
}
